import { useRef, useEffect, useState, useCallback } from "react";
import Editor from "@monaco-editor/react";
import prettier from "prettier";
import babelParser from "prettier/plugins/babel";
import esTree from "prettier/plugins/estree";
// @ts-ignore - This import is handled by vite-plugin-raw
import playwrightDefs from "../../types/playwright.d.ts?raw";
// @ts-ignore
import plutosdkDefs from "../../types/plutosdk.d.ts?raw";
import { DEFAULT_SCRIPT } from "../../constants";
import hljs from "highlight.js";
import { formatWithRuff } from "./PythonFormatter";
import { useClerk } from "@clerk/clerk-react";

export function normalizePastedTemplate(src: string): string {
  if (!src.includes("\\`")) return src;

  const lang =
    hljs.highlightAuto(src, ["javascript", "typescript"]).language ?? "";

  if (!lang.startsWith("javascript")) {
    return src;
  }

  const looksWrapped = src.startsWith("`") && src.endsWith("`");
  let code = looksWrapped ? src.slice(1, -1) : src;

  if (looksWrapped) {
    code = code.replace(/\\n/g, "\n").replace(/\\r/g, "\r");
  }
  return code.replace(/\\`/g, "`");
}

type EditorProps = {
  onUpdate: (code: string) => void;
  onHasChanges: (hasChanges: boolean) => void;
  hasChanges: boolean;
  editorHeight: number;
  editorRef: React.RefObject<any>;
  setLastChangeHadSpace: React.Dispatch<React.SetStateAction<boolean>>;
};

const ReactEditor = (props: EditorProps) => {
  const { editorRef } = props;
  const [currentLanguage, setCurrentLanguage] = useState("javascript");
  // State to hold the formatted initial code
  const [formattedInitialCode, setFormattedInitialCode] = useState(
    // localStorage.getItem("demo-ui-code") || DEFAULT_SCRIPT.script,
    DEFAULT_SCRIPT.script,
  );

  const monacoSvcRef = useRef<any>(null); // Ref to store the monaco services object
  const initialCodeRef = useRef(formattedInitialCode.trim());

  const clerk = useClerk();

  // Format the code using Prettier or Ruff
  const formatCode = async (codeToFormat: string, language: string) => {
    codeToFormat = normalizePastedTemplate(codeToFormat);
    try {
      if (language === "python") {
        try {
          return formatWithRuff(codeToFormat);
        } catch (e) {
          console.error("Ruff formatting error:", e);
          return codeToFormat;
        }
      } else {
        // Only normalize line endings. Remove other custom JS string manipulations
        let processedCode = codeToFormat.replace(/\r\n/g, "\n");

        try {
          const result = await prettier.format(processedCode, {
            parser: "babel",
            plugins: [babelParser, esTree],
            printWidth: 80,
            babelParserPlugins: ["topLevelAwait"],
            tabWidth: 2,
            singleQuote: true,
            trailingComma: "es5",
          });
          return result;
        } catch (prettierError) {
          console.error("Prettier.format specific error:", prettierError);
          return codeToFormat;
        }
      }
    } catch (e) {
      console.error(
        `Formatter error (formatCode catch-all, lang: ${language}):`,
        e,
      );
      return codeToFormat;
    }
  };

  const refresh = async () => {
    if (!editorRef.current) return;

    const defaultScriptContent = DEFAULT_SCRIPT.script.trim();
    const detectedLang =
      hljs.highlightAuto(defaultScriptContent, ["javascript", "python"])
        .language || "javascript";

    if (currentLanguage !== detectedLang) {
      setCurrentLanguage(detectedLang);
    }
    const model = editorRef.current.getModel();
    if (model && monacoSvcRef.current) {
      monacoSvcRef.current.editor.setModelLanguage(model, detectedLang);
    }

    if (editorRef.current && model) {
      const fullRange = model.getFullModelRange();
      editorRef.current.executeEdits("reset", [
        { range: fullRange, text: defaultScriptContent },
      ]);

      await formatEditorContent(detectedLang);

      const formattedDefaultScript = editorRef.current.getValue();
      initialCodeRef.current = formattedDefaultScript.trim();
      props.onUpdate(initialCodeRef.current);
      props.onHasChanges(false);
      localStorage.setItem("demo-ui-code", formattedDefaultScript);
      editorRef.current.resetScript();
    }
  };

  // Format the editor content
  const formatEditorContent = async (langOverride?: string) => {
    if (!editorRef.current) return "";

    const currentCode = editorRef.current.getValue();
    const languageToUse = langOverride || currentLanguage;

    const formattedCode = await formatCode(currentCode, languageToUse);

    const model = editorRef.current.getModel();
    if (model && formattedCode.trim() !== currentCode.trim()) {
      const fullRange = model.getFullModelRange();
      editorRef.current.executeEdits("formatter", [
        { range: fullRange, text: formattedCode },
      ]);
    }
    return formattedCode;
  };

  // Expose the save and run function for parent components
  const handleSaveAndRun = useCallback(async () => {
    try {
      if (!editorRef.current) return;

      const currentCodeValue = editorRef.current.getValue();
      const detectedLangOnSave =
        hljs.highlightAuto(currentCodeValue, ["javascript", "python"])
          .language || "javascript";

      if (currentLanguage !== detectedLangOnSave) {
        setCurrentLanguage(detectedLangOnSave);
        const model = editorRef.current.getModel();
        if (model && monacoSvcRef.current) {
          monacoSvcRef.current.editor.setModelLanguage(
            model,
            detectedLangOnSave,
          );
        }
      }

      await formatEditorContent(detectedLangOnSave);

      const finalCodeToSave = editorRef.current.getValue().trim();
      props.onUpdate(finalCodeToSave);
      props.setLastChangeHadSpace((l) => !l);
      initialCodeRef.current = finalCodeToSave;
      props.onHasChanges(false);
      localStorage.setItem("demo-ui-code", finalCodeToSave);
    } catch (error) {
      console.error("Failed to save and run code: ", error);
    }
  }, [
    currentLanguage,
    props.onUpdate,
    props.onHasChanges,
    formatEditorContent,
    props.setLastChangeHadSpace,
  ]);

  // Format initial code on component mount
  useEffect(() => {
    async function preFormatInitialCode() {
      const initialContent = DEFAULT_SCRIPT.script;
      const detectedLang =
        hljs.highlightAuto(initialContent, ["javascript", "python"]).language ||
        "javascript";
      if (currentLanguage !== detectedLang) {
        setCurrentLanguage(detectedLang);
      }

      const formatted = await formatCode(initialContent, detectedLang); // Pass language directly
      setFormattedInitialCode(formatted);
      initialCodeRef.current = formatted.trim();
    }

    preFormatInitialCode();
  }, []);

  useEffect(() => {
    // Set up once, clean up on unmount
    const observer = new MutationObserver((mutations) => {
      const elements = [
        `[style*="z-index: 2147483647"]`,
        `div[data-lastpass-root]`,
        `div.icloud-password-field`,
        `div.__1password`,
        `div.__lp-inline-edit-element`,
      ];

      elements.forEach((selector) => {
        const element = document.querySelector(selector);
        if (element) {
          element.remove();
        }
      });
    });

    // Start observing with a configuration
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    // Clean up
    return () => observer.disconnect();
  }, []);

  const handleBeforeMount = (monaco) => {
    monacoSvcRef.current = monaco;
    monaco.languages.typescript.javascriptDefaults.setEagerModelSync(true);
    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES2020,
      allowNonTsExtensions: true,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      noEmit: true,
      lib: ["es2022"],
      typeRoots: ["node_modules/@types"],
    });

    monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: true,
      noSyntaxValidation: false,
      diagnosticCodesToIgnore: [2304, 2552, 2580, 2581, 2582, 2583, 2584],
    });

    monaco.languages.typescript.javascriptDefaults.addExtraLib(
      playwrightDefs,
      "playwright.d.ts",
    );

    monaco.languages.typescript.javascriptDefaults.addExtraLib(
      plutosdkDefs,
      "plutosdk.d.ts",
    );

    monaco.languages.typescript.javascriptDefaults.addExtraLib(
      `
      // Basic console APIs
      declare var console: {
        log(...data: any[]): void;
        error(...data: any[]): void;
        warn(...data: any[]): void;
        info(...data: any[]): void;
      };`,
      "console.d.ts",
    );

    // Register completion item provider to prioritize our APIs
    monaco.languages.registerCompletionItemProvider("javascript", {
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        // If we're importing or referencing Playwright, prioritize those suggestions
        const textUntilPosition = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        });

        // If typing after 'import { ' or 'from "play', prioritize Playwright
        if (
          /import\s+{\s*$/.test(textUntilPosition) ||
          /from\s+["']play/.test(textUntilPosition)
        ) {
          return {
            suggestions: [
              {
                label: "chromium",
                kind: monaco.languages.CompletionItemKind.Module,
                documentation: "Playwright Chromium module",
                insertText: "chromium",
                range: range,
                sortText: "0", // Force it to the top with a low sort value
              },
            ],
          };
        }

        return { suggestions: [] };
      },
    });
  };

  const changeScript = async (s: string) => {
    if (!editorRef.current) return;

    const detectedLang =
      hljs.highlightAuto(s, ["javascript", "python"]).language || "javascript";
    setCurrentLanguage(detectedLang);

    const model = editorRef.current.getModel?.();
    if (model && monacoSvcRef.current) {
      monacoSvcRef.current.editor.setModelLanguage(model, detectedLang);
    }

    await editorRef.current.setValue(s);
    await handleSaveAndRun();
  };

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = {
      editor,
      save: handleSaveAndRun,
      getCode: getCurrentCode,
      formatCode: formatEditorContent,
      refresh,
      getValue: () => editor.getValue(),
      setValue: (value) => editor.setValue(value),
      getModel: () => editor.getModel(),
      executeEdits: (source, edits) => editor.executeEdits(source, edits),
      changeScript,
    };

    /**
     * We need to debounce because when we reset script or
     * select a new script, it triggers some fast script changes
     * that cause issues/flickers.  This allows react to resolve the
     * updates it needs to
     */
    let debounceTimeout;
    editor.onDidChangeModelContent(() => {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        const currentCode = editor.getValue().trim();
        props.onHasChanges(currentCode !== initialCodeRef.current);
      }, 50);
    });

    editor.onDidPaste(async (e) => {
      const model = editor.getModel();
      const monacoInstance = monacoSvcRef.current;

      if (!model || !monacoInstance) return;

      const currentFullContent = normalizePastedTemplate(model.getValue());

      try {
        const langOfFullDocument =
          hljs.highlightAuto(currentFullContent, ["javascript", "python"])
            .language || "javascript";

        if (currentLanguage !== langOfFullDocument) {
          setCurrentLanguage(langOfFullDocument);
          monacoInstance.editor.setModelLanguage(model, langOfFullDocument);
        }

        if (editorRef.current) {
          editorRef.current.setValue(currentFullContent);
          await new Promise((resolve) => setTimeout(resolve, 0));

          // Diagnostic: Check if Monaco's getValue() differs after setValue + delay
          const codeAfterSetValueAndDelay = editorRef.current.getValue();
        }

        await formatEditorContent(langOfFullDocument); // Pass the just-detected language
      } catch (error) {
        console.warn(
          "Error during onPaste processing (full format with langOverride, normalization and delay):",
          error,
        );
      }
    });

    // Add keyboard shortcut for formatting (Ctrl+S / Cmd+S)
    editor.addCommand(
      monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
      async (e) => {
        // Prevent the default save action
        e?.preventDefault?.();
        handleSaveAndRun();
      },
    );

    editor.addCommand(
      monaco.KeyMod.Alt | monaco.KeyMod.Shift | monaco.KeyCode.KeyF,
      async () => {
        await formatEditorContent();
      },
    );

    // Disable password manager popups
    const editorDomNode = editor.getDomNode();
    if (editorDomNode) {
      const inputs = editorDomNode.querySelectorAll(
        "textarea, input, div[contenteditable=true]",
      );
      inputs.forEach((input) => {
        input.setAttribute("data-lpignore", "true");
        input.setAttribute("autocomplete", "off");
        input.setAttribute("data-form-type", "other");
        input.setAttribute("autocorrect", "off");
        input.setAttribute("autocapitalize", "off");
        input.setAttribute("spellcheck", "false");
      });

      editorDomNode.setAttribute("data-lpignore", "true");
    }
  };

  // Get the current code for the "Lets get started" button
  const getCurrentCode = () => {
    if (editorRef.current) {
      return editorRef.current.getValue();
    }
    return formattedInitialCode;
  };

  return (
    <div className="flex w-full h-full">
      {/* 6/30/25 - @monaco-editor/react script conflicts with clerk script - see docs */}
      {/* https://clerk.com/docs/troubleshooting/script-loading */}
      {clerk.loaded && (
        <Editor
          height={props.editorHeight}
          language={currentLanguage}
          defaultValue={formattedInitialCode}
          theme="vs-light"
          beforeMount={handleBeforeMount}
          onMount={handleEditorDidMount}
          options={{
            wordBasedSuggestions: "currentDocument",
            wordBasedSuggestionsOnlySameLanguage: true,
            minimap: { enabled: false },
            formatOnPaste: false,
            renderLineHighlight: "none",
            overviewRulerBorder: false,
            scrollbar: {
              vertical: "hidden",
              horizontal: "hidden",
            },
            quickSuggestions: {
              other: true,
              comments: false,
              strings: false,
            },
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnEnter: "on",
            tabCompletion: "on",
            suggest: {
              showFields: true,
              showFunctions: true,
              showConstructors: false,
              showMethods: true,
              showVariables: true,
              showClasses: false,
              showStructs: false,
              showInterfaces: false,
              showEnums: false,
              showEnumMembers: false,
              showKeywords: true,
              showProperties: true,
              showOperators: false,
            },
            domReadOnly: false,
          }}
        />
      )}
    </div>
  );
};

export default ReactEditor;
